import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Image, X, Upload, Trash2, Check } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import CameraCapture from './CameraCapture';

const PhotoArmoireManager = ({ emplacement, onUpdate, onClose }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(emplacement.photo_armoire_url);

  // Upload photo vers Supabase Storage
  const uploadPhoto = async (blob) => {
    try {
      setUploading(true);
      
      // Nom fichier unique
      const fileName = `armoire_${emplacement.id}_${Date.now()}.jpg`;
      const filePath = `armoires/${fileName}`;

      console.log('📤 Upload photo armoire:', filePath);

      // Upload vers bucket outils-photos
      const { error: uploadError } = await supabase.storage
        .from('outils-photos')
        .upload(filePath, blob, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Récupérer URL publique
      const { data: { publicUrl } } = supabase.storage
        .from('outils-photos')
        .getPublicUrl(filePath);

      console.log('✅ URL publique:', publicUrl);

      // Supprimer ancienne photo si existe
      if (emplacement.photo_armoire_url) {
        const oldPath = emplacement.photo_armoire_url.split('/armoires/')[1];
        if (oldPath) {
          await supabase.storage
            .from('outils-photos')
            .remove([`armoires/${oldPath}`]);
          console.log('🗑️ Ancienne photo supprimée');
        }
      }

      // Sauvegarder URL en BDD
      const { error: updateError } = await supabase
        .from('mat_emplacements')
        .update({ photo_armoire_url: publicUrl })
        .eq('id', emplacement.id);

      if (updateError) throw updateError;

      setPreviewUrl(publicUrl);
      console.log('✅ Photo armoire sauvegardée');
      alert('✅ Photo de l\'armoire ajoutée !');
      
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('❌ Erreur upload:', error);
      alert('❌ Erreur lors de l\'ajout de la photo : ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Capture photo via caméra
  const handleCameraCapture = async (blob) => {
    setShowCamera(false);
    await uploadPhoto(blob);
  };

  // Upload fichier depuis PC
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('⚠️ Veuillez sélectionner une image');
      return;
    }

    await uploadPhoto(file);
  };

  // Supprimer photo
  const handleDelete = async () => {
    if (!confirm('Supprimer la photo de l\'armoire ?')) return;

    try {
      setUploading(true);

      // Supprimer du storage
      if (emplacement.photo_armoire_url) {
        const path = emplacement.photo_armoire_url.split('/armoires/')[1];
        if (path) {
          await supabase.storage
            .from('outils-photos')
            .remove([`armoires/${path}`]);
        }
      }

      // Supprimer URL de la BDD
      const { error } = await supabase
        .from('mat_emplacements')
        .update({ photo_armoire_url: null })
        .eq('id', emplacement.id);

      if (error) throw error;

      setPreviewUrl(null);
      console.log('✅ Photo armoire supprimée');
      alert('✅ Photo supprimée');
      
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      alert('❌ Erreur : ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-40 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-slate-900 rounded-xl max-w-2xl w-full"
        >
          {/* En-tête */}
          <div className="bg-slate-800 px-6 py-4 flex items-center justify-between rounded-t-xl">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Image className="w-7 h-7 text-purple-400" />
              Photo de l'armoire
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Contenu */}
          <div className="p-6 space-y-6">
            {/* Info */}
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Image className="w-5 h-5 text-blue-400" />
                {emplacement.nom}
              </h3>
              <p className="text-gray-300 text-sm">
                Prenez une photo de l'armoire complète pour l'inventaire visuel
              </p>
            </div>

            {/* Aperçu photo existante */}
            {previewUrl && (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Photo armoire"
                  className="w-full rounded-lg"
                />
                <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded text-sm font-semibold">
                  ✓ Photo actuelle
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              {/* Bouton caméra */}
              <button
                onClick={() => setShowCamera(true)}
                disabled={uploading}
                className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              >
                <Camera className="w-6 h-6" />
                {previewUrl ? 'Reprendre une photo' : 'Prendre une photo'}
              </button>

              {/* Bouton upload fichier */}
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                />
                <div className="w-full px-6 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all cursor-pointer">
                  <Upload className="w-6 h-6" />
                  Choisir une image depuis l'ordinateur
                </div>
              </label>

              {/* Bouton supprimer */}
              {previewUrl && (
                <button
                  onClick={handleDelete}
                  disabled={uploading}
                  className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                >
                  <Trash2 className="w-5 h-5" />
                  Supprimer la photo
                </button>
              )}
            </div>

            {/* Loading */}
            {uploading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 mx-auto mb-3"></div>
                <p className="text-white">Traitement en cours...</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Caméra */}
      <AnimatePresence>
        {showCamera && (
          <CameraCapture
            onCapture={handleCameraCapture}
            onClose={() => setShowCamera(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PhotoArmoireManager;